/**
 * Converts a resolved FloorPlanRoom into a 3D mesh: the floor and ceiling
 * slabs with typed floor surfaces (sceneSlabs), walls (primary edges, alcove
 * walls, corner chamfers) carrying door/window/opening cutouts (wallShell),
 * the recess behind each niche alcove (sceneNiches), and the free elements
 * (sceneElements).
 *
 * Walls are built from the same line frames the 2D editor uses, so the 3D model
 * always agrees with the plan. Each wall is a culled surface (see mesh3d.ts);
 * floor and solid objects are always-visible.
 */
import {
  EDGE_NAMES,
  edgeFrame,
  type FloorPlanFreeElement,
  type FloorPlanRoom,
  type Point,
  resolveScene,
  roomCeilingHeight,
  WALL_THICKNESS,
} from '@kaiizen/kai-floor-plan';
import { PALETTE, type Selection } from '../editor-model';
import { type Vec3 } from '../math3d';
import { wallOuterRing } from '../room-geometry';
import { type SceneGadget } from './gadget';
import { MeshBuilder, type SceneDoor, type SceneSolid, type SceneWall } from './mesh3d';
import { type Pickable3D } from './pick3d';
import { type PlanToWorld } from './sceneBox';
import { buildElements, floorPickables } from './sceneElements';
import { buildNiches, nicheShapes } from './sceneNiches';
import { buildCeiling, buildFloor } from './sceneSlabs';
import { buildWalls } from './wallShell';

/**
 * The floor slab's least thickness: what a room standing on its own, or the
 * lowest room on a level, gets. Floor, ceiling and walls share one thickness.
 */
export const FLOOR_SLAB_THICKNESS = WALL_THICKNESS;

/** A text label anchored to a world point (e.g. an object's name on its top face). */
export interface SceneLabel {
  text: string;
  pos: Vec3;
  /** What the label names, for a host that draws the labels itself: the assembler's room id. */
  id?: string;
}

/**
 * Everything a 3D view needs from one build. `S` is the pickables' selection
 * type: the editor's `Selection` for a room built by `buildScene3D`, the
 * assembler's room selection for a composed multi-room scene.
 */
export interface Scene3D<S = Selection> {
  mesh: MeshBuilder;
  /** Everything solid the view draws, as plan-space paths + extrusion spans. */
  solids: SceneSolid[];
  /** The primary walls, each one continuous solid carrying its cutouts. */
  walls: SceneWall[];
  /** Every door's box, the index each door's surfaces and panel solids carry. */
  doors: SceneDoor[];
  /** Plan-space bounds of the model (for camera framing). */
  bbox: { minX: number; minY: number; maxX: number; maxY: number };
  center: { x: number; y: number };
  ceiling: number;
  /** Room-facing (inward) world normal of each primary wall, indexed by EDGE_NAMES. */
  wallNormals: Vec3[];
  /** A representative world point on each primary wall (midpoint, mid-height). */
  wallPoints: Vec3[];
  /** Object-name labels, anchored just above each named object's top face. */
  labels: SceneLabel[];
  /** Click targets (doors/windows/openings + free elements) for 3D selection. */
  pickables: Pickable3D<S>[];
  /** The move gadget's anchor when the selection is a free element. */
  gadget?: SceneGadget;
  /** The assembler's selected room's center, mid-height, where its gadget stands: the camera centers on it when a room is selected. */
  selectionCenter?: Vec3;
}

/**
 * The surface group of the ceiling slab: the fifth cullable group after the
 * four primary walls (`wallNormals[CEILING_GROUP]` is its downward normal).
 */
export const CEILING_GROUP = EDGE_NAMES.length;

export interface Scene3DOptions {
  floorDepth?: number;
  /** Which interior elements to build; all when absent. Hidden ones keep their indices for picking and selection. */
  showElement?: (element: FloorPlanFreeElement) => boolean;
}

/**
 * Each cull group's room-facing world normal and a world point on it: the
 * four primary walls in EDGE_NAMES order, at each wall's midpoint half way
 * up, then the ceiling, facing down from its center. The view decides each
 * group per frame by the sign of dot3d(normal, eye − point), so three of the
 * four walls can show at once, and the ceiling only from below.
 */
function cullGroupFrames(shape: FloorPlanRoom, ceiling: number, toWorld: PlanToWorld): Pick<Scene3D, 'wallNormals' | 'wallPoints'> {
  const frames = EDGE_NAMES.map(edge => edgeFrame(edge, shape.width, shape.length));
  return {
    wallNormals: [...frames.map(({ perp }): Vec3 => [-perp.x, 0, -perp.y]), [0, -1, 0]],
    wallPoints: [
      ...frames.map(({ start, dir, length }) => toWorld(start.x + dir.x * (length / 2), start.y + dir.y * (length / 2), ceiling / 2)),
      toWorld(shape.width / 2, shape.length / 2, ceiling),
    ],
  };
}

/** Plan bounds of the built model: the slabs' outer perimeter, or the room's rings when it has no walkable boundary. */
function modelBounds(outerRing: Point[], roomSolid: Point[][][]): Scene3D['bbox'] {
  const points = outerRing.length ? outerRing : roomSolid.flatMap(polygon => polygon[0]);
  const xCoordinates = points.map(point => point.x), yCoordinates = points.map(point => point.y);
  return { minX: Math.min(...xCoordinates), minY: Math.min(...yCoordinates), maxX: Math.max(...xCoordinates), maxY: Math.max(...yCoordinates) };
}

/**
 * `floorDepth` is how far the floor slab reaches below the floor (ft). The
 * assembler deepens a raised room's slab so every room on a level shares one
 * underside, leaving no gap beneath the raised ones.
 */
export function buildScene3D(
  shape: FloorPlanRoom,
  selection?: Selection,
  { floorDepth = FLOOR_SLAB_THICKNESS, showElement }: Scene3DOptions = {},
): Scene3D {
  const ceiling = roomCeilingHeight(shape);
  const center = { x: shape.width / 2, y: shape.length / 2 };
  const toWorld: PlanToWorld = (x, y, height) => [x - center.x, height, y - center.y];
  const shapes = nicheShapes(shape);
  const floorScene = resolveScene(shapes.floor);
  const ceilingScene = shapes.ceiling === shapes.floor ? floorScene : resolveScene(shapes.ceiling);
  const mesh = new MeshBuilder();
  const wallPickables: Pickable3D[] = [];
  const { wallNormals, wallPoints } = cullGroupFrames(shape, ceiling, toWorld);
  buildFloor(mesh, shape, floorScene, wallOuterRing(shapes.floor), toWorld, floorDepth, selection);
  buildCeiling(mesh, ceilingScene, wallOuterRing(shapes.ceiling), toWorld, ceiling, CEILING_GROUP);
  buildWalls(mesh, shapes.walls, toWorld, ceiling, wallNormals.slice(0, EDGE_NAMES.length), PALETTE.wall, wallPickables, selection);
  buildNiches(mesh, shape, toWorld);
  const elements = buildElements(mesh, shape, toWorld, selection, showElement);
  return {
    mesh,
    solids: mesh.solids,
    walls: mesh.walls,
    doors: mesh.doors,
    bbox: modelBounds(wallOuterRing(shapes.walls), floorScene.roomSolid),
    center,
    ceiling,
    wallNormals,
    wallPoints,
    labels: elements.labels,
    pickables: [...wallPickables, ...elements.pickables, ...floorPickables(shape, floorScene.roomSolid, toWorld)],
    gadget: elements.gadget,
  };
}
